import type { ReturnRequestLogDto } from "@babackapp/private-api-client/types";
import type { DebugLogDto } from "@babackapp/private-api-client/client/use-get-debug-logs";
import ActivityListItem from "@/pages/(app)/returns/view/components/return-activity/activity-list-item";
import DebugLogActivity from "@/pages/(app)/returns/view/components/return-activity/contents/debug-log-activity";
import { getResentFromLogId } from "@/pages/(app)/returns/view/components/return-activity/contents/resend-email";
import { useMemo } from "react";

type ActivityEntry =
  | { kind: "activity"; data: ReturnRequestLogDto }
  | { kind: "debug"; data: DebugLogDto };

type ReturnRequestActivityListProps = {
  activities: ReturnRequestLogDto[];
  debugLogs?: DebugLogDto[];
  isShowTechnicalDetails?: boolean;
  returnRequestId: string;
};

export default function ActivityList({
  activities,
  debugLogs,
  isShowTechnicalDetails,
  returnRequestId,
}: ReturnRequestActivityListProps) {
  const entries = useMemo(() => {
    const items: ActivityEntry[] = activities.map((a) => ({
      kind: "activity" as const,
      data: a,
    }));

    if (debugLogs) {
      for (const log of debugLogs) {
        items.push({ kind: "debug" as const, data: log });
      }
    }

    return items.sort((a, b) => {
      const dateA = a.kind === "activity" ? a.data.date : a.data.createdAt;
      const dateB = b.kind === "activity" ? b.data.date : b.data.createdAt;
      return dateA > dateB ? -1 : 1;
    });
  }, [activities, debugLogs]);

  // Latest resend of each email entry, for the 5-minute resend cooldown.
  const lastResendAtByLogId = useMemo(() => {
    const map = new Map<string, number>();
    for (const activity of activities) {
      const resentFromLogId = getResentFromLogId(activity);
      if (!resentFromLogId) continue;
      map.set(
        resentFromLogId,
        Math.max(map.get(resentFromLogId) ?? 0, activity.date),
      );
    }
    return map;
  }, [activities]);

  return (
    <div
      className={"relative flex flex-col gap-1"}
      id="return-request-activities"
    >
      <div className={"absolute left-3 top-0 bottom-2 w-[1px] bg-slate-200"} />
      {entries.map((entry) => {
        if (entry.kind === "debug") {
          return <DebugLogActivity key={entry.data.id} debugLog={entry.data} />;
        }
        return (
          <ActivityListItem
            key={entry.data.id}
            activity={entry.data}
            returnRequestId={returnRequestId}
            lastResendAt={lastResendAtByLogId.get(entry.data.id)}
            isShowTechnicalDetails={isShowTechnicalDetails}
          />
        );
      })}
    </div>
  );
}
